import { defineStore } from 'pinia';

export const useQuoteStore = defineStore('quote', {
    state: () => ({
        loading: true,
        showSidebar: false,
        showEditQuoteModal: false,
        showCustomizeModal: false,
        showDiscountModal: false,
        isSavingQuote: false,
        addProductToggle: false,
        showEmailModal: false,
        quoteName: "Window/Door Quote",
        quoteObj: {},
        products: [],
        frames: [
            { id: 1, image: "https://picsum.photos/200/150", price: 250.00, customizations: [] }
        ],
        selectedFrame: null,
        settings: {},
        branding: {},
        // Discount management
        discountOptions: [], // Available discount options from API
        globalDiscount: {
            type: 'percentage', // 'percentage' or 'fixed'
            value: 0,
            isUplift: false // false for discount, true for uplift
        },
        frameDiscounts: {}, // { frameId: { type, value, isUplift } } - always an object, never an array
        discountSummary: {
            totalOriginal: 0,
            totalDiscount: 0,
            totalFinal: 0
        }
    }),
    getters: {
        // Ensure frameDiscounts is always an object
        safeFrameDiscounts(state) {
            if (!state.frameDiscounts || Array.isArray(state.frameDiscounts)) {
                return {};
            }
            return state.frameDiscounts;
        }
    },
    actions: {
        setLoading (value) {
            this.loading = value
        },
        setProducts(products) {
          this.products = products
        },
        setSettings(settings){
          this.settings = settings
        },
        addProduct(product) {
            this.frames.push({
                id: this.frames.length + 1,
                image: product.image,
                price: 300.00,
                customizations: []
            });
            this.showSidebar = false;
        },
        saveCustomizations(customizations) {
            this.selectedFrame.customizations = customizations;
            this.showCustomizeModal = false;
        },
        customizeFrame(frame) {
            this.selectedFrame = frame;
            this.showCustomizeModal = true;
        },
        setQuoteObj (quote) {
            this.quoteObj = quote
            this.quoteName = quote.quote_name

            if (quote.discount_options) {
                this.discountOptions = quote.discount_options.discountOptions;
                let discountOptions = quote.discount_options.discountOptions;
                
                if (typeof discountOptions === 'string') {
                    try {
                        discountOptions = JSON.parse(discountOptions);
                    } catch (e) {
                        console.error('Error parsing discountOptions:', e);
                        discountOptions = null;
                    }
                }
                
                if (discountOptions) {
                    if (discountOptions.globalDiscount) {
                        this.globalDiscount = discountOptions.globalDiscount;
                    }
                    
                    if (discountOptions.frameDiscounts) {
                        let frameDiscounts = discountOptions.frameDiscounts;
                        if (Array.isArray(frameDiscounts)) {
                            frameDiscounts = {};
                        }
                        this.frameDiscounts = frameDiscounts;
                    }
                }
            }

        },
        setFrames (frames) {
            this.frames = frames
        },
        setBranding (branding) {
            this.branding = branding;
        },
        applyGlobalDiscount(type, value, isUplift = false) {
            this.globalDiscount = {
                type: type,
                value: parseFloat(value) || 0,
                isUplift: isUplift
            };
            this.updateDiscountSummary();
        },
        applyFrameDiscount(frameId, type, value, isUplift = false) {
            if (!this.frameDiscounts || Array.isArray(this.frameDiscounts)) {
                this.frameDiscounts = {};
            }
            
            this.frameDiscounts[frameId] = {
                type: type,
                value: parseFloat(value) || 0,
                isUplift: isUplift
            };
            this.updateDiscountSummary();
        },
        removeFrameDiscount(frameId) {
            delete this.frameDiscounts[frameId];
            this.updateDiscountSummary();
        },
        clearAllDiscounts() {
            this.globalDiscount = {
                type: 'percentage',
                value: 0,
                isUplift: false
            };
            this.frameDiscounts = {};
            this.updateDiscountSummary();
        },
        calculateDiscountedPrice(originalPrice, discount) {
            if (!discount || discount.value === 0) {
                return originalPrice;
            }
            
            let discountAmount = 0;
            if (discount.type === 'percentage') {
                discountAmount = originalPrice * (discount.value / 100);
            } else {
                discountAmount = discount.value;
            }
            
            if (discount.isUplift) {
                return originalPrice + discountAmount;
            } else {
                return Math.max(0, originalPrice - discountAmount);
            }
        },
        updateDiscountSummary() {
            let totalOriginal = 0;
            let totalFinal = 0;
            
            this.frames.forEach(frame => {
                const frameTotal = parseFloat(frame.price) * (frame.quantity || 1);
                totalOriginal += frameTotal;
                
                // Apply frame-specific discount if exists
                let finalPrice = frameTotal;
                if (this.frameDiscounts[frame.id]) {
                    finalPrice = this.calculateDiscountedPrice(frameTotal, this.frameDiscounts[frame.id]);
                }
                
                // Apply global discount if no frame-specific discount
                else if (this.globalDiscount.value > 0) {
                    finalPrice = this.calculateDiscountedPrice(frameTotal, this.globalDiscount);
                }
                
                totalFinal += finalPrice;
            });
            
            this.discountSummary = {
                totalOriginal: totalOriginal,
                totalDiscount: totalOriginal - totalFinal,
                totalFinal: totalFinal
            };
        },
        getFrameDiscountedPrice(frame) {
            const frameTotal = parseFloat(frame.price) * (frame.quantity || 1);
            
            if (this.frameDiscounts[frame.id]) {
                return this.calculateDiscountedPrice(frameTotal, this.frameDiscounts[frame.id]);
            }
            
            if (this.globalDiscount.value > 0) {
                return this.calculateDiscountedPrice(frameTotal, this.globalDiscount);
            }
            
            return frameTotal;
        },
        hasActiveDiscounts() {
            return this.globalDiscount.value > 0 || Object.keys(this.frameDiscounts).length > 0;
        },
        setIsSavingQuote(value) {
            this.isSavingQuote = value;
        }
    }
});
